'use client';

import { useState, useEffect } from 'react';
import { SignIn } from '@clerk/nextjs';
import { LuX } from 'react-icons/lu';
import Link from 'next/link';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal = ({ isOpen, onClose }: LoginModalProps) => {
  const [mounted, setMounted] = useState(false);

  // Handle ESC key press to close modal
  useEffect(() => {
    setMounted(true);

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      // Prevent scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  if (!mounted) return null;

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      {/* Backdrop */}
      <div
        className='fixed inset-0 bg-black/70 backdrop-blur-xs transition-opacity'
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className='relative z-10 w-full max-w-md scale-100 opacity-100 transform transition-all'>
        {/* Close Button */}
        <button
          onClick={onClose}
          className='absolute -top-2 -right-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-gray-800 text-gray-400 transition-colors hover:text-white hover:bg-gray-700'
        >
          <LuX className='h-5 w-5' />
        </button>

        {/* Content Card with Styling */}
        <div className='relative overflow-hidden rounded-2xl'>
          {/* Gradient Border */}
          <div className='absolute inset-0 rounded-2xl bg-linear-to-r from-blue-500/40 via-purple-500/40 to-emerald-500/40 p-px'>
            <div className='absolute inset-0 rounded-2xl bg-gray-900/95 backdrop-blur-xl' />
          </div>

          {/* Floating Elements */}
          <div className='absolute -top-4 -right-4 h-24 w-24 rounded-full bg-blue-500/30 blur-xl' />
          <div className='absolute -bottom-4 -left-4 h-24 w-24 rounded-full bg-purple-500/30 blur-xl' />

          {/* Inner Content */}
          <div className='relative p-6 md:p-8'>
            <div className='mb-4 text-center'>
              <img
                src='/images/logo_clear.png'
                alt='QwetuLands Logo'
                className='mx-auto h-12 w-auto mb-3'
              />
              <h2 className='text-2xl font-bold'>
                <span className='bg-linear-to-r from-blue-400 via-purple-400 to-emerald-400 bg-clip-text text-transparent'>
                  Welcome Back
                </span>
              </h2>
              <p className='mt-1 text-sm text-gray-300'>
                Sign in to access your QwetuLands account
              </p>
            </div>

            {/* Clerk Sign-In Component */}
            <SignIn
              appearance={{
                elements: {
                  rootBox: 'w-full',
                  card: 'bg-transparent shadow-none w-full p-0 m-0',
                  header: 'hidden',
                  footer: 'hidden',
                  footerAction: 'hidden',
                  formButtonPrimary:
                    'bg-linear-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600 text-white font-medium rounded-lg px-4 py-3 w-full hover:scale-[1.02] transition-all hover:shadow-lg',
                  formFieldInput:
                    'bg-gray-800/50 border border-gray-700/50 rounded-lg p-3 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500/20 hover:border-blue-500/70',
                  formFieldLabel: 'text-gray-200',
                  footerActionLink: 'text-blue-400 hover:text-blue-300',
                  identityPreviewEditButton:
                    'text-blue-500 hover:text-blue-400',
                  alertText: 'text-gray-200',
                  socialButtonsBlockButton:
                    'border border-gray-700 hover:border-gray-600 text-gray-200 hover:bg-gray-800/50',
                  socialButtonsBlockButtonText: 'text-gray-200',
                  formFieldAction: 'text-blue-400 hover:text-blue-300',
                  formFieldSuccessText: 'text-emerald-500',
                  otpCodeFieldInput: 'bg-gray-800 border-gray-700 text-white',
                  dividerLine: 'bg-gray-700',
                  dividerText: 'text-gray-400',
                },
                layout: {
                  socialButtonsVariant: 'iconButton',
                  socialButtonsPlacement: 'bottom',
                },
              }}
              signUpUrl='/sign-up'
            />

            {/* Custom Footer */}
            <div className='mt-6 pt-4 border-t border-gray-700/30 text-center'>
              <p className='text-gray-400 text-sm'>
                Don't have an account?{' '}
                <Link
                  href='/sign-up'
                  className='text-blue-400 hover:text-blue-300 transition-colors'
                  onClick={() => {
                    onClose();
                  }}
                >
                  Sign up
                </Link>
              </p>
              <p className='text-gray-500 text-xs mt-2'>
                By signing in, you agree to our Terms and Privacy Policy
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
